import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const emptyJob = {
  title: '',
  company: '',
  location: '',
  job_type: 'full-time',
  description: '',
  requirements: [],
  salary_range: '',
  is_published: true
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');

  const [jobs, setJobs] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [talents, setTalents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(emptyJob);
  const [requirementInput, setRequirementInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchJobs();
    fetchIntakes();
    fetchTalents();
  }, []);

  const fetchIntakes = async () => {
    try {
      const response = await axios.get(`${API}/admin/intake`, { withCredentials: true });
      setIntakes(response.data);
    } catch (error) {
      console.error('Error fetching intakes:', error);
    }
  };

  const fetchTalents = async () => {
    try {
      const response = await axios.get(`${API}/admin/talent`, { withCredentials: true });
      setTalents(response.data);
    } catch (error) {
      console.error('Error fetching talents:', error);
    }
  };

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/jobs`, {
        withCredentials: true
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        ...job,
        requirements: job.requirements || []
      });
    } else {
      setEditingJob(null);
      setFormData(emptyJob);
    }
    setRequirementInput('');
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingJob(null);
    setFormData(emptyJob);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addRequirement = () => {
    if (requirementInput.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, requirementInput.trim()]
      }));
      setRequirementInput('');
    }
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.company || !formData.location || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      if (editingJob) {
        await axios.put(`${API}/admin/jobs/${editingJob.id}`, formData, {
          withCredentials: true
        });
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/admin/jobs`, formData, {
          withCredentials: true
        });
        toast.success('Job created successfully');
      }
      handleCloseDialog();
      fetchJobs();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, {
        withCredentials: true
      });
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const togglePublish = async (job) => {
    try {
      await axios.put(`${API}/admin/jobs/${job.id}`, {
        is_published: !job.is_published
      }, {
        withCredentials: true
      });
      toast.success(job.is_published ? 'Job unpublished' : 'Job published');
      fetchJobs();
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error('Failed to update job');
    }
  };

  return (
    <div className="pt-44 min-h-screen bg-background" data-testid="admin-dashboard">
      <div className="container-main py-12 md:py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <p className="caption mb-2">Admin Dashboard</p>
              <h1 className="heading-2">Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-1">Signed in as {user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 border border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-colors rounded"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-border" data-testid="admin-tabs">
            {[
              { key: 'jobs', label: 'Jobs', count: jobs.length },
              { key: 'intake', label: 'Contact Forms', count: intakes.length },
              { key: 'talent', label: 'Talent Applications', count: talents.length },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
                  activeTab === tab.key
                    ? 'border-foreground text-foreground font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label} <span className="ml-1 text-xs text-muted-foreground">({tab.count})</span>
              </button>
            ))}
          </div>

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <div>
            <div className="flex justify-end mb-4">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    onClick={() => handleOpenDialog()}
                    className="btn-primary"
                    data-testid="add-job-btn"
                  >
                  <Plus className="h-4 w-4" /> Add Job
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">
                    {editingJob ? 'Edit Job' : 'Add New Job'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Job Title <span className="text-destructive">*</span>
                      </label>
                      <Input
                        value={formData.title}
                        onChange={(e) => updateField('title', e.target.value)}
                        placeholder="e.g., Social Media Manager"
                        data-testid="job-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Company <span className="text-destructive">*</span>
                      </label>
                      <Input
                        value={formData.company}
                        onChange={(e) => updateField('company', e.target.value)}
                        placeholder="Company name"
                        data-testid="job-company-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Location <span className="text-destructive">*</span>
                      </label>
                      <Input
                        value={formData.location}
                        onChange={(e) => updateField('location', e.target.value)}
                        placeholder="e.g., Remote, NYC, etc."
                        data-testid="job-location-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Job Type <span className="text-destructive">*</span>
                      </label>
                      <Select
                        value={formData.job_type}
                        onValueChange={(value) => updateField('job_type', value)}
                      >
                        <SelectTrigger data-testid="job-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full-time">Full-time</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="fractional">Fractional</SelectItem>
                          <SelectItem value="freelance">Freelance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Salary Range (Optional)
                    </label>
                    <Input
                      value={formData.salary_range}
                      onChange={(e) => updateField('salary_range', e.target.value)}
                      placeholder="e.g., $80k - $100k"
                      data-testid="job-salary-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Description <span className="text-destructive">*</span>
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => updateField('description', e.target.value)}
                      placeholder="Job description..."
                      className="min-h-[120px]"
                      data-testid="job-description-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Requirements
                    </label>
                    <div className="flex gap-2 mb-3">
                      <Input
                        value={requirementInput}
                        onChange={(e) => setRequirementInput(e.target.value)}
                        placeholder="Add a requirement"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                        data-testid="job-requirement-input"
                      />
                      <button
                        type="button"
                        onClick={addRequirement}
                        className="btn-secondary px-4"
                        data-testid="add-requirement-btn"
                      >
                        Add
                      </button>
                    </div>
                    {formData.requirements.length > 0 && (
                      <ul className="space-y-2">
                        {formData.requirements.map((req, index) => (
                          <li key={index} className="flex items-center justify-between bg-secondary p-2 rounded">
                            <span className="text-sm">{req}</span>
                            <button
                              type="button"
                              onClick={() => removeRequirement(index)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Publish immediately</label>
                    <Switch
                      checked={formData.is_published}
                      onCheckedChange={(checked) => updateField('is_published', checked)}
                      data-testid="job-publish-switch"
                    />
                  </div>

                  <div className="flex justify-end gap-4 pt-4 border-t">
                    <button
                      type="button"
                      onClick={handleCloseDialog}
                      className="btn-secondary"
                      data-testid="cancel-job-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="btn-primary"
                      data-testid="save-job-btn"
                    >
                      {isSaving ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
                      ) : editingJob ? (
                        'Update Job'
                      ) : (
                        'Create Job'
                      )}
                    </button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>

          {/* Jobs Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20" data-testid="admin-loading">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20 bg-secondary border border-border" data-testid="admin-empty">
              <h3 className="heading-3 mb-4">No jobs yet</h3>
              <p className="text-muted-foreground mb-6">Create your first job posting to get started.</p>
            </div>
          ) : (
            <div className="bg-background border border-border overflow-hidden" data-testid="admin-jobs-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job, index) => (
                    <TableRow key={job.id} data-testid={`admin-job-row-${index}`}>
                      <TableCell className="font-medium">{job.title}</TableCell>
                      <TableCell>{job.company}</TableCell>
                      <TableCell>{job.location}</TableCell>
                      <TableCell className="capitalize">{job.job_type}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => togglePublish(job)}
                          className={`flex items-center gap-1 text-sm ${
                            job.is_published ? 'text-green-600' : 'text-muted-foreground'
                          }`}
                          data-testid={`toggle-publish-${index}`}
                        >
                          {job.is_published ? (
                            <><Eye className="h-4 w-4" /> Published</>
                          ) : (
                            <><EyeOff className="h-4 w-4" /> Draft</>
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenDialog(job)}
                            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                            data-testid={`edit-job-${index}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(job.id)}
                            className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                            data-testid={`delete-job-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          </div>
          )}

          {/* Intake Forms Tab */}
          {activeTab === 'intake' && (
            <div>
              {intakes.length === 0 ? (
                <div className="text-center py-20 bg-secondary border border-border" data-testid="intake-empty">
                  <h3 className="heading-3 mb-4">No contact forms yet</h3>
                  <p className="text-muted-foreground">Submissions from the Book a Call page will appear here.</p>
                </div>
              ) : (
                <div className="bg-background border border-border overflow-hidden" data-testid="intake-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Roles</TableHead>
                        <TableHead>Timeline</TableHead>
                        <TableHead>Budget</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {intakes.map((intake, i) => (
                        <TableRow key={intake.id} data-testid={`intake-row-${i}`}>
                          <TableCell className="font-medium">{intake.company_name}</TableCell>
                          <TableCell>{intake.contact_name}</TableCell>
                          <TableCell>
                            <a href={`mailto:${intake.email}`} className="text-[#C8A96A] hover:underline">{intake.email}</a>
                          </TableCell>
                          <TableCell>{intake.hiring_roles?.join(', ')}</TableCell>
                          <TableCell>{intake.timeline}</TableCell>
                          <TableCell>{intake.budget_range}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(intake.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Talent Applications Tab */}
          {activeTab === 'talent' && (
            <div>
              {talents.length === 0 ? (
                <div className="text-center py-20 bg-secondary border border-border" data-testid="talent-empty">
                  <h3 className="heading-3 mb-4">No talent applications yet</h3>
                  <p className="text-muted-foreground">Applications from the Join page will appear here.</p>
                </div>
              ) : (
                <div className="bg-background border border-border overflow-hidden" data-testid="talent-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Specialization</TableHead>
                        <TableHead>Experience</TableHead>
                        <TableHead>Availability</TableHead>
                        <TableHead>Portfolio</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {talents.map((talent, i) => (
                        <TableRow key={talent.id} data-testid={`talent-row-${i}`}>
                          <TableCell className="font-medium">{talent.full_name}</TableCell>
                          <TableCell>
                            <a href={`mailto:${talent.email}`} className="text-[#C8A96A] hover:underline">{talent.email}</a>
                          </TableCell>
                          <TableCell>{talent.specialization?.join(', ')}</TableCell>
                          <TableCell className="capitalize">{talent.experience_level}</TableCell>
                          <TableCell className="capitalize">{talent.availability}</TableCell>
                          <TableCell>
                            {talent.portfolio_url && (
                              <a href={talent.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-[#C8A96A] hover:underline text-sm">View</a>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(talent.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

        </motion.div>
      </div>
    </div>
  );
}
